// Pure aggregation helpers for benchmark charts. No React / no Redis deps,
// so this module loads both in the CRA client (webpack import) and in the
// node test runner (require). CommonJS on purpose.

const MAX_POINTS = 80
const RESERVED_KEYS = new Set(['total', 'byProxy'])

// Strip the aggregate keys from a success-/fail-{prefix} object, leaving only
// the per-instance series (keyed by INSTANCE_NAME).
function stripReserved (obj) {
  const out = {}
  for (const k of Object.keys(obj || {})) {
    if (!RESERVED_KEYS.has(k)) out[k] = obj[k]
  }
  return out
}

// Build chart points + summary from per-key success/fail slot maps.
// successByKey / failByKey: { [key]: { [slotTs]: count } }
// Returns { chartData, keys (volume>0, sorted by total desc), summary }.
function buildSeries ({ successByKey, failByKey, hours, now }) {
  // Guard: non-object inputs (undefined / null / scalar from a stale bundle or a
  // shape drift in the benchmark payload) must not produce NaN/undefined slots.
  const isObj = (v) => v && typeof v === 'object' && !Array.isArray(v)
  const sBy = isObj(successByKey) ? successByKey : {}
  const fBy = isObj(failByKey) ? failByKey : {}
  const keysSet = new Set([...Object.keys(sBy), ...Object.keys(fBy)])

  const cutoff = now - hours * 3600 * 1000
  const slotSet = new Set()
  for (const k of keysSet) {
    for (const s of Object.keys(sBy[k] || {})) if (Number(s) > cutoff) slotSet.add(Number(s))
    for (const s of Object.keys(fBy[k] || {})) if (Number(s) > cutoff) slotSet.add(Number(s))
  }
  const slots = Array.from(slotSet).filter(s => Number.isFinite(s)).sort((a, b) => a - b)

  const groupSize = Math.max(1, Math.ceil(slots.length / MAX_POINTS))
  const points = []
  for (let i = 0; i < slots.length; i += groupSize) {
    const group = slots.slice(i, i + groupSize)
    const ts = group[Math.floor(group.length / 2)]
    const point = { ts }
    for (const k of keysSet) {
      let s = 0
      let f = 0
      for (const slot of group) {
        s += (sBy[k] && sBy[k][slot]) || 0
        f += (fBy[k] && fBy[k][slot]) || 0
      }
      const total = s + f
      point[`${k}__rate`] = total > 0 ? (s / total) * 100 : null
      point[`${k}__vol`] = total
      point[`${k}__s`] = s
      point[`${k}__f`] = f
    }
    points.push(point)
  }

  const summary = Array.from(keysSet).map(k => {
    let s = 0
    let f = 0
    for (const slot of Object.keys(sBy[k] || {})) if (Number(slot) > cutoff) s += sBy[k][slot]
    for (const slot of Object.keys(fBy[k] || {})) if (Number(slot) > cutoff) f += fBy[k][slot]
    const total = s + f
    return { key: k, success: s, fail: f, total, rate: total > 0 ? (s / total) * 100 : null }
  }).sort((a, b) => b.total - a.total)

  const keys = summary.filter(x => x.total > 0).map(x => x.key)

  return { chartData: points, keys, summary }
}

// Given an array of env objects, return the Set of keys whose value differs
// across the array (a missing key counts as a distinct value).
function diffKeys (envs) {
  const list = envs || []
  const allKeys = new Set()
  for (const e of list) for (const k of Object.keys(e || {})) allKeys.add(k)
  const diff = new Set()
  for (const k of allKeys) {
    const vals = new Set(list.map(e => (e ? e[k] : undefined)))
    if (vals.size > 1) diff.add(k)
  }
  return diff
}

module.exports = { buildSeries, stripReserved, diffKeys, MAX_POINTS }
